// src/client/components/shared/FormField.tsx
import React from 'react';
import { Label } from '@client/components/ui/label';
import { Input } from '@client/components/ui/input';
import { Textarea } from '@client/components/ui/textarea';
import { Switch } from '@client/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@client/components/ui/select';

type FormFieldType = 'text' | 'number' | 'textarea' | 'switch' | 'select' | 'color';

interface SelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface FormFieldProps {
  id: string;
  label: string;
  type?: FormFieldType;
  value: string | number | boolean;
  onChange: (value: string | boolean) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  hint?: string;
  options?: SelectOption[]; // For select type
  rows?: number; // For textarea
  className?: string;
}

export function FormField({
  id,
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  required = false,
  disabled = false,
  error,
  hint,
  options = [],
  rows = 3,
  className = '',
}: FormFieldProps) {
  const renderInput = () => {
    switch (type) {
      case 'switch':
        return (
          <div className="flex items-center space-x-2">
            <Switch
              id={id}
              checked={Boolean(value)}
              onCheckedChange={onChange}
              disabled={disabled}
            />
            <Label htmlFor={id} className="cursor-pointer">
              {label}
            </Label>
          </div>
        );

      case 'select':
        return (
          <Select
            value={String(value)}
            onValueChange={onChange}
            disabled={disabled}
          >
            <SelectTrigger id={id}>
              <SelectValue placeholder={placeholder || `Select ${label}`} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.icon ? (
                    <div className="flex items-center gap-2">
                      {option.icon}
                      {option.label}
                    </div>
                  ) : (
                    option.label
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'textarea':
        return (
          <Textarea
            id={id}
            value={String(value)}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            rows={rows}
            className={error ? 'border-red-500' : ''}
          />
        );

      case 'color':
        return (
          <Input
            id={id}
            type="color"
            value={String(value)}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={`w-16 ${error ? 'border-red-500' : ''}`}
          />
        );

      case 'number':
        return (
          <Input
            id={id}
            type="number"
            value={String(value)}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className={error ? 'border-red-500' : ''}
          />
        );

      default: // text
        return (
          <Input
            id={id}
            type="text"
            value={String(value)}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className={error ? 'border-red-500' : ''}
          />
        );
    }
  };

  // Switch type handles its own label
  if (type === 'switch') {
    return (
      <div className={`space-y-2 ${className}`}>
        {renderInput()}
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <Label htmlFor={id}>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      {renderInput()}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}